import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, Info, ChevronDown, BookA, Search } from "lucide-react";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "name", label: "Names" },
  { key: "word", label: "Words & Phrases" },
];

// Diacritic-insensitive first letter for A–Z grouping
const firstLetter = (term) =>
  (term || "?")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .charAt(0)
    .toUpperCase();

export default function YorubaDictionary() {
  useSEO(
    "Yorùbá Dictionary — Browse Names & Words A–Z",
    "Browse the ÀsÀNigHT Worldwide Yorùbá dictionary A–Z. Explore hand-verified Yorùbá names and everyday words with meanings, literal breakdowns, pronunciation and cultural notes."
  );

  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("all");
  const [term, setTerm] = React.useState("");
  const [open, setOpen] = React.useState(null);

  React.useEffect(() => {
    api
      .get("/yoruba/dictionary")
      .then((r) => setEntries(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = entries.filter((e) => {
    if (filter !== "all" && e.type !== filter) return false;
    if (term.trim()) {
      const q = term.trim().toLowerCase();
      return (
        e.term.toLowerCase().includes(q) ||
        (e.meaning || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Group A–Z
  const groups = {};
  filtered.forEach((e) => {
    const l = firstLetter(e.term);
    (groups[l] = groups[l] || []).push(e);
  });
  const letters = Object.keys(groups).sort();
  const presentSet = new Set(letters);
  const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const jumpTo = (l) => {
    const el = document.getElementById(`dict-letter-${l}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-night text-cream min-h-screen">
      {/* HERO */}
      <header className="max-w-5xl mx-auto px-6 pt-24 pb-10">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <BookA className="w-4 h-4 text-gold" />
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Yorùbá Dictionary</p>
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="dictionary-title">
            Browse Names & Words, A–Z
          </h1>
          <p className="text-cream/70 text-lg leading-relaxed max-w-2xl">
            Explore our growing collection of hand-verified Yorùbá names and everyday
            expressions. Tap any entry to reveal its meaning, breakdown, pronunciation, and
            cultural story.
          </p>
        </Reveal>
      </header>

      {/* CONTROLS */}
      <div className="max-w-5xl mx-auto px-6 pb-8 space-y-5">
        <Reveal>
          <div className="relative">
            <Search className="w-4 h-4 text-cream/40 absolute left-5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Filter the list…"
              data-testid="dictionary-filter-input"
              className="w-full rounded-full bg-night-surface border border-cream/20 pl-12 pr-6 py-3.5 text-cream placeholder:text-cream/40 focus:outline-none focus:border-gold transition-colors"
            />
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`dictionary-filter-${f.key}`}
                className={`text-sm rounded-full px-5 py-2 border transition-colors ${
                  filter === f.key
                    ? "bg-gold text-night border-gold font-semibold"
                    : "border-cream/20 text-cream/75 hover:border-gold hover:text-gold"
                }`}
              >
                {f.label}
              </button>
            ))}
            <span className="ml-auto self-center text-sm text-cream/45" data-testid="dictionary-count">
              {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
            </span>
          </div>
        </Reveal>
      </div>

      {/* A–Z JUMP BAR */}
      {!loading && letters.length > 0 && (
        <div className="sticky top-[102px] z-30 bg-night/90 backdrop-blur-md border-y border-cream/10" data-testid="dictionary-jump-bar">
          <div className="max-w-5xl mx-auto px-6 py-3 flex flex-wrap gap-1.5 justify-center">
            {ALPHABET.map((l) => {
              const active = presentSet.has(l);
              return (
                <button
                  key={l}
                  disabled={!active}
                  onClick={() => jumpTo(l)}
                  data-testid={`dictionary-jump-${l}`}
                  className={`w-8 h-8 rounded-full text-sm font-semibold transition-colors ${
                    active
                      ? "text-cream hover:bg-gold hover:text-night"
                      : "text-cream/25 cursor-not-allowed"
                  }`}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* LIST */}
      <div className="max-w-5xl mx-auto px-6 pb-24">
        {loading ? (
          <p className="text-cream/50 py-16 text-center">Loading dictionary…</p>
        ) : filtered.length === 0 ? (
          <p className="text-cream/50 py-16 text-center" data-testid="dictionary-empty">
            No entries match your filter yet.
          </p>
        ) : (
          letters.map((letter) => (
            <section key={letter} id={`dict-letter-${letter}`} className="mb-10 scroll-mt-[170px]" data-testid={`dictionary-group-${letter}`}>
              <div className="flex items-center gap-4 mb-4">
                <span className="grid place-items-center w-10 h-10 rounded-full bg-gold text-night font-serif text-xl">
                  {letter}
                </span>
                <div className="h-px flex-1 bg-cream/10" />
              </div>
              <div className="space-y-3">
                {groups[letter].map((e) => {
                  const isOpen = open === e.term;
                  return (
                    <div
                      key={e.term}
                      className="border border-cream/12 rounded-xl bg-night-surface overflow-hidden"
                      data-testid={`dictionary-entry-${e.term}`}
                    >
                      <button
                        onClick={() => setOpen(isOpen ? null : e.term)}
                        className="w-full flex items-center gap-4 text-left px-6 py-5 hover:bg-cream/5 transition-colors"
                        data-testid={`dictionary-toggle-${e.term}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-3">
                            <h3 className="font-serif text-2xl text-gold">{e.term}</h3>
                            <span className="text-[10px] uppercase tracking-widest border border-cream/25 rounded-full px-2.5 py-0.5 text-cream/60">
                              {e.type === "name" ? "Name" : "Word"}
                            </span>
                            {e.gender && (
                              <span className="text-[10px] uppercase tracking-widest border border-cream/25 rounded-full px-2.5 py-0.5 text-cream/60">
                                {e.gender}
                              </span>
                            )}
                          </div>
                          <p className="text-cream/70 mt-1 line-clamp-1">{e.meaning}</p>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-cream/50 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-6 pt-1 space-y-5 border-t border-cream/10">
                              {e.pronunciation && (
                                <p className="flex items-center gap-2 text-cream/60 text-sm">
                                  <Volume2 className="w-4 h-4 text-gold-soft" />
                                  <span className="italic">{e.pronunciation}</span>
                                </p>
                              )}
                              <Field label="Meaning" value={e.meaning} />
                              {e.breakdown && <Field label="Literal Breakdown" value={e.breakdown} />}
                              {e.example && <Field label="Example Usage" value={e.example} italic />}
                              {e.cultural_note && (
                                <div className="flex items-start gap-3 border-t border-cream/10 pt-5">
                                  <Info className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                                  <p className="text-cream/75 leading-relaxed">{e.cultural_note}</p>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>

      <Newsletter />
    </div>
  );
}

const Field = ({ label, value, italic }) => (
  <div>
    <p className="uppercase tracking-[0.2em] text-[11px] text-gold-soft mb-1.5">{label}</p>
    <p className={`text-cream/85 leading-relaxed ${italic ? "italic" : ""}`}>{value}</p>
  </div>
);
